import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../../../hooks/useUsers";
import { useSmsLogs } from "../../../hooks/useSmsLogs";
import { useSmsSettings } from "../../../hooks/useSmsSettings";
import { useSmsTemplates } from "../../../hooks/useSmsTemplates";
import {
  clearSessionUser,
  mustForcePasswordChange,
  readSessionUser,
} from "../../../lib/authSession";
import { sendManualSms } from "../../../lib/smsService";
import {
  SMS_TEMPLATE_LABELS,
  isSmsTemplateToggleable,
  type SmsTemplateKey,
} from "../../../lib/smsTemplates";

const TEMPLATE_ORDER: SmsTemplateKey[] = [
  "welcomeTempPassword",
  "passwordReset",
  "slaughterStarted",
  "slaughterDone",
  "deliveryAssigned",
  "deliveryStarted",
  "deliveryCompleted",
  "paymentReceived",
];

function formatDateTime(value?: string) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function channelBadge(channel: string) {
  if (channel === "marketing") {
    return "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200";
  }

  if (channel === "auth") {
    return "bg-sky-50 text-sky-700 border-sky-200";
  }

  return "bg-amber-50 text-amber-700 border-amber-200";
}

function statusBadge(status: string) {
  if (status === "sent") {
    return "bg-farm-green-pale text-farm-green border-farm-green/30";
  }

  if (status === "disabled") {
    return "bg-gray-100 text-gray-600 border-gray-300";
  }

  if (status === "queued") {
    return "bg-farm-gold-pale text-farm-gold border-farm-gold/30";
  }

  return "bg-red-50 text-red-600 border-red-200";
}

function Label({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="block text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-1">
      {children}
      {required && <span className="text-farm-earth ml-0.5">*</span>}
    </label>
  );
}

export default function SmsAyarlariPage() {
  const navigate = useNavigate();
  const { users } = useUsers();
  const { logs, clearSmsLogs } = useSmsLogs();
  const { settings, updateSmsSettings, resetSmsSettings } = useSmsSettings();
  const {
    templates,
    templateEnabledMap,
    updateSmsTemplate,
    updateSmsTemplateEnabled,
    resetSmsTemplates,
  } = useSmsTemplates();

  const [ready, setReady] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState(
    "NetGSM test mesajı - Evren Çiftliği SMS merkezi hazır."
  );
  const [sendingTest, setSendingTest] = useState(false);
  const [savedTemplateKey, setSavedTemplateKey] = useState<SmsTemplateKey | null>(
    null
  );

  useEffect(() => {
    const sessionUser = readSessionUser();

    if (!sessionUser) {
      navigate("/giris", { replace: true });
      return;
    }

    const currentUser = users.find((item) => item.id === sessionUser.id);

    if (!currentUser || currentUser.durum !== "Aktif") {
      clearSessionUser();
      navigate("/giris", { replace: true });
      return;
    }

    if (mustForcePasswordChange(currentUser)) {
      navigate("/sifre-degistir", { replace: true });
      return;
    }

    if (currentUser.rol !== "Admin" && currentUser.rol !== "Muhasebe") {
      navigate("/panel/hayvanlar", { replace: true });
      return;
    }

    setReady(true);
  }, [navigate, users]);

  useEffect(() => {
    setTestPhone(settings.testPhone);
  }, [settings.testPhone]);

  const stats = useMemo(() => {
    return {
      total: logs.length,
      sent: logs.filter((item) => item.status === "sent").length,
      disabled: logs.filter((item) => item.status === "disabled").length,
      failed: logs.filter((item) => item.status === "failed").length,
    };
  }, [logs]);

  const recentLogs = useMemo(() => logs.slice(0, 20), [logs]);

  async function handleToggle(
    key:
      | "isActive"
      | "operationalEnabled"
      | "marketingEnabled"
      | "iysEnabled"
  ) {
    await updateSmsSettings({
      [key]: !settings[key],
    });
  }

  async function handleSaveTestPhone() {
    await updateSmsSettings({
      testPhone: testPhone.trim(),
    });
  }

  async function handleSendTestSms() {
    if (!testPhone.trim()) {
      window.alert("Test telefonu zorunludur.");
      return;
    }

    if (!testMessage.trim()) {
      window.alert("Test mesajı boş olamaz.");
      return;
    }

    setSendingTest(true);

    try {
      await updateSmsSettings({
        testPhone: testPhone.trim(),
      });

      await sendManualSms({
        channel: "auth",
        eventType: "manual_test_sms",
        phone: testPhone.trim(),
        message: testMessage.trim(),
      });

      window.alert("Test SMS logu oluşturuldu.");
    } finally {
      setSendingTest(false);
    }
  }

  async function handleResetAll() {
    const ok = window.confirm(
      "SMS ayarları ve SMS şablonları varsayılanlara döndürülerek sıfırlansın mı?"
    );
    if (!ok) return;

    await resetSmsSettings();
    await resetSmsTemplates();
  }

  async function handleClearLogs() {
    const ok = window.confirm("SMS logları temizlensin mi?");
    if (!ok) return;

    await clearSmsLogs();
  }

  if (!ready) return null;

  return (
    <div className="min-h-full bg-farm-cream font-body">
      <div className="max-w-[1700px] mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              SMS Ayarları
            </h1>
            <p className="text-xs text-farm-muted">
              NetGSM altyapısı, kanal izinleri, şablonlar ve gönderim logları
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => void handleResetAll()}
              className="h-9 px-3 rounded-full border border-farm-border bg-farm-warm text-farm-muted text-xs font-semibold hover:border-farm-earth/40"
            >
              Varsayılanlara Dön
            </button>

            <button
              type="button"
              onClick={() => void handleClearLogs()}
              className="h-9 px-3 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100"
            >
              Logları Temizle
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {[
            {
              label: "Toplam Log",
              value: stats.total,
              color: "text-farm-brown",
              bg: "bg-farm-earth-pale",
              icon: "ri-message-2-line",
            },
            {
              label: "Gönderildi",
              value: stats.sent,
              color: "text-farm-green",
              bg: "bg-farm-green-pale",
              icon: "ri-check-line",
            },
            {
              label: "Kapalı / İzin Yok",
              value: stats.disabled,
              color: "text-gray-600",
              bg: "bg-gray-100",
              icon: "ri-pause-circle-line",
            },
            {
              label: "Başarısız",
              value: stats.failed,
              color: "text-red-600",
              bg: "bg-red-50",
              icon: "ri-close-circle-line",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-farm-warm border border-farm-border rounded-farm px-3 py-3 flex items-center gap-3"
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.bg} ${item.color}`}
              >
                <i className={`${item.icon} text-base`} />
              </div>
              <div>
                <p className={`text-xl font-bold leading-none ${item.color}`}>
                  {item.value}
                </p>
                <p className="text-[10px] text-farm-muted mt-0.5">
                  {item.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[430px_minmax(0,1fr)] gap-4">
          <div className="space-y-4">
            <div className="bg-farm-warm border border-farm-border rounded-farm p-4 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-farm-earth-pale text-farm-earth flex items-center justify-center">
                  <i className="ri-settings-3-line text-sm" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-farm-brown">
                    Sistem Modu
                  </p>
                  <p className="text-[11px] text-farm-muted">
                    Genel açık / kapalı ve kanal izinleri
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  {
                    key: "isActive" as const,
                    title: "SMS sistemi aktif",
                    desc: "Kapalıysa hiçbir SMS gönderilmez, sadece disabled log oluşur.",
                    value: settings.isActive,
                  },
                  {
                    key: "operationalEnabled" as const,
                    title: "Operasyonel SMS aktif",
                    desc: "Taşıma, kesim ve ödeme bilgilendirmeleri için kullanılır.",
                    value: settings.operationalEnabled,
                  },
                  {
                    key: "marketingEnabled" as const,
                    title: "Reklam / bilgilendirme aktif",
                    desc: "Toplu tanıtım veya pazarlama gönderimleri için.",
                    value: settings.marketingEnabled,
                  },
                  {
                    key: "iysEnabled" as const,
                    title: "İYS hazırlığı aktif",
                    desc: "Şimdilik bilgi amaçlı alan. Gerçek İYS entegrasyonu sonraki adım.",
                    value: settings.iysEnabled,
                  },
                ].map((item) => (
                  <div
                    key={item.key}
                    className="rounded-farm border border-farm-border bg-farm-cream px-3 py-3 flex items-start justify-between gap-3"
                  >
                    <div>
                      <p className="text-xs font-semibold text-farm-brown">
                        {item.title}
                      </p>
                      <p className="text-[10px] text-farm-muted mt-0.5 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => void handleToggle(item.key)}
                      className={`h-7 min-w-[62px] rounded-full border text-[10px] font-semibold px-2 transition-colors ${
                        item.value
                          ? "bg-farm-green text-white border-farm-green"
                          : "bg-white text-farm-muted border-farm-border"
                      }`}
                    >
                      {item.value ? "Açık" : "Kapalı"}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-farm-warm border border-farm-border rounded-farm p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-farm-brown">
                  NetGSM / Gönderici Bilgileri
                </p>
                <p className="text-[11px] text-farm-muted mt-0.5">
                  Şimdilik mock gönderim logları üretilir, yine de panel ayarları kalıcıdır.
                </p>
              </div>

              <div>
                <Label>NetGSM Alt Kullanıcı</Label>
                <input
                  value={settings.netgsmSubUser}
                  onChange={(e) =>
                    void updateSmsSettings({ netgsmSubUser: e.target.value })
                  }
                  className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
                  placeholder="netgsm kullanıcı"
                />
              </div>

              <div>
                <Label>NetGSM Şifre</Label>
                <input
                  type="password"
                  value={settings.netgsmPassword}
                  onChange={(e) =>
                    void updateSmsSettings({ netgsmPassword: e.target.value })
                  }
                  className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
                  placeholder="******"
                />
              </div>

              <div>
                <Label>Gönderici Başlığı</Label>
                <input
                  value={settings.senderTitle}
                  onChange={(e) =>
                    void updateSmsSettings({ senderTitle: e.target.value })
                  }
                  className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
                  placeholder="EVREN"
                />
              </div>
            </div>

            <div className="bg-farm-warm border border-farm-border rounded-farm p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-farm-brown">
                  Test Gönderimi
                </p>
                <p className="text-[11px] text-farm-muted mt-0.5">
                  Bu bölüm gerçek gönderim yerine log oluşturur.
                </p>
              </div>

              <div>
                <Label required>Test Telefonu</Label>
                <input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  onBlur={() => void handleSaveTestPhone()}
                  className="w-full h-10 px-3 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth"
                  placeholder="05xx xxx xx xx"
                />
              </div>

              <div>
                <Label required>Test Mesajı</Label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth resize-none"
                />
              </div>

              <button
                type="button"
                onClick={() => void handleSendTestSms()}
                disabled={sendingTest}
                className="w-full h-10 rounded-full bg-farm-earth text-white text-sm font-semibold hover:bg-farm-brown disabled:opacity-60"
              >
                {sendingTest ? "Gönderiliyor..." : "Test SMS Logu Oluştur"}
              </button>
            </div>
          </div>

          <div className="space-y-4 min-w-0">
            <div className="bg-farm-warm border border-farm-border rounded-farm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-farm-green-pale text-farm-green flex items-center justify-center">
                  <i className="ri-file-list-3-line text-sm" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-farm-brown">
                    SMS Şablonları
                  </p>
                  <p className="text-[11px] text-farm-muted">
                    Dinamik mesaj metinleri burada yönetilir
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {TEMPLATE_ORDER.map((templateKey) => {
                  const toggleable = isSmsTemplateToggleable(templateKey);
                  const enabled = templateEnabledMap[templateKey] !== false;

                  return (
                    <div
                      key={templateKey}
                      className="rounded-farm border border-farm-border bg-white p-3 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-xs font-semibold text-farm-brown">
                            {SMS_TEMPLATE_LABELS[templateKey]}
                          </p>
                          <p className="text-[10px] text-farm-muted mt-0.5">
                            Kod: {templateKey}
                          </p>
                        </div>

                        {toggleable ? (
                          <button
                            type="button"
                            onClick={() =>
                              void updateSmsTemplateEnabled(templateKey, !enabled)
                            }
                            className={`h-7 min-w-[70px] px-2 rounded-full border text-[10px] font-semibold transition-colors ${
                              enabled
                                ? "bg-farm-green text-white border-farm-green"
                                : "bg-white text-farm-muted border-farm-border"
                            }`}
                          >
                            {enabled ? "Açık" : "Kapalı"}
                          </button>
                        ) : (
                          <span className="inline-flex items-center h-7 px-2 rounded-full border border-farm-border bg-farm-cream text-[10px] font-semibold text-farm-muted">
                            Zorunlu
                          </span>
                        )}
                      </div>

                      <textarea
                        value={templates[templateKey]}
                        onChange={(e) =>
                          void updateSmsTemplate(templateKey, e.target.value)
                        }
                        onBlur={() => {
                          setSavedTemplateKey(templateKey);
                          setTimeout(() => setSavedTemplateKey(null), 1200);
                        }}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-farm-border rounded-xl bg-farm-cream text-farm-brown outline-none focus:border-farm-earth resize-none"
                      />

                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-[10px] text-farm-muted leading-relaxed">
                          Kullanılabilir alanlar: {"{{customerName}}"}, {"{{password}}"},
                          {" {{kupeNo}}"}, {" {{carrierName}}"}, {" {{carrierPhone}}"},
                          {" {{address}}"}, {" {{currentBalance}}"}, {" {{paidAmount}}"},
                          {" {{remainingBalance}}"}
                        </p>

                        {savedTemplateKey === templateKey && (
                          <span className="text-[10px] font-semibold text-farm-green">
                            Kaydedildi
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-farm-warm border border-farm-border rounded-farm overflow-hidden">
              <div className="px-4 py-3 border-b border-farm-border bg-farm-cream">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-farm-earth-pale text-farm-earth flex items-center justify-center">
                    <i className="ri-history-line text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-farm-brown">
                      Son SMS Logları
                    </p>
                    <p className="text-[11px] text-farm-muted">
                      Son 20 kayıt gösterilir
                    </p>
                  </div>
                </div>
              </div>

              {recentLogs.length === 0 ? (
                <div className="py-10 text-center text-sm text-farm-muted">
                  Log kaydı yok
                </div>
              ) : (
                <div className="divide-y divide-farm-border">
                  {recentLogs.map((log) => (
                    <div key={log.id} className="px-4 py-3 space-y-2">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${channelBadge(
                                log.channel
                              )}`}
                            >
                              {log.channel}
                            </span>

                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusBadge(
                                log.status
                              )}`}
                            >
                              {log.status}
                            </span>
                          </div>

                          <p className="text-xs font-semibold text-farm-brown mt-1">
                            {log.phone}
                          </p>
                          <p className="text-[11px] text-farm-muted mt-0.5">
                            {log.eventType}
                          </p>
                        </div>

                        <div className="text-right text-[10px] text-farm-muted">
                          <p>{formatDateTime(log.sentAt || log.createdAt)}</p>
                        </div>
                      </div>

                      <div className="rounded-xl border border-farm-border bg-farm-cream px-3 py-2">
                        <p className="text-xs text-farm-brown leading-relaxed">
                          {log.message}
                        </p>
                      </div>

                      {log.providerResponse && (
                        <p className="text-[10px] text-farm-muted">
                          Sağlayıcı: {log.providerResponse}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}